import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MobileStepper,
  Slide,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import makeStyles from "@mui/styles/makeStyles";
import { useSnackbar } from "notistack";
import { Feature, Map } from "ol";
import GeometryType from "ol/geom/GeometryType";
import Polygon from "ol/geom/Polygon";
import { Draw, Modify, Snap } from "ol/interaction";
import { Vector } from "ol/source";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { If } from "react-extras";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import {
  PortCreateDto,
  PortDetailsDto,
  PortEditDto,
  usePortApi,
} from "~/api/GeneratedApi";
import { useAppSelector } from "~/redux/store/store";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import { addPortsToMap } from "../../functions/addPortsToMap";
import createPortLayer from "../../functions/createPortLayer";
import { findLayerByTitle } from "../../functions/findLayerByTitle";
import useCreateMap from "../../hooks/useCreateMap";
import { LayerTitle } from "../../types/layers";
import PortForm, { PortFormInputs } from "./PortForm";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "95%",
    maxHeight: "95%",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
  },
  contentContainer: {
    display: "flex",
    height: "80%",
    maxHeight: "80%",
  },
  mapContainer: {
    height: "100%",
    width: "100%",
  },
  drawText: {
    width: "80%",
    margin: `${theme.spacing(1)} auto`,
  },
  drawActionButton: {
    display: "block",
    margin: "0 auto",
    color: theme.palette.warning.contrastText,
    backgroundColor: theme.palette.warning.main,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  onActionDone: () => void;
  mainMap?: Map;
} & ({ edit: true; port: PortDetailsDto } | { edit?: never; port?: never });

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PortDialog = ({
  open,
  onClose,
  onActionDone,
  edit,
  port,
  mainMap,
}: Props): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation("portDialog");
  const mapRef = useRef<Map>();
  const drawRef = useRef<Draw>();
  const sourceRef = useRef<Vector>();
  const [entered, setEntered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<PortFormInputs>();
  const [drawFeature, setDrawFeature] = useState<Feature<Polygon>>();
  const [drawDone, setDrawDone] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const apiErrorHandler = useApiErrorHandler();
  const { createMap } = useCreateMap();
  const storePorts = useAppSelector((s) => s.data.ports);

  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));

  const portApi = usePortApi();

  const action = useMutation(
    edit && port
      ? (portToEdit: PortEditDto) => portApi.editPort(portToEdit, port.id)
      : (newPort: PortCreateDto) => portApi.addPort(newPort),
  );

  // If editing, set form and draw values to the port to edit
  useEffect(() => {
    if (!edit || !port) return;
    setFormData({
      name: port.name ?? "",
      description: port.description ?? "",
      locodeId: port.locode?.id ?? null,
      mapAreaIds: port.mapAreas?.map((ma) => ma.mapAreaId) ?? [],
    });
    setDrawFeature(
      new Feature<Polygon>({
        geometry: new Polygon([port.points.map((point) => [point.x, point.y])]),
        name: port.name,
      }),
    );
    setDrawDone(true);
  }, [edit, port]);

  const addMap = useCallback(() => {
    if (containerRef.current && !mapRef.current) {
      mapRef.current = createMap({
        target: containerRef.current,
        includedLayers: [LayerTitle.OSM, LayerTitle.Ports],
        popup: false,
        existingMap: mapRef.current,
      });
      // Add a layer for drawing the port
      const { layer: drawLayer, source: drawSource } = createPortLayer(true);
      sourceRef.current = drawSource;
      mapRef.current.addLayer(drawLayer);

      // Set the existing ports layer as visible, ignoring the actual settings
      findLayerByTitle(mapRef.current, LayerTitle.Ports)?.setVisible(true);

      drawRef.current = new Draw({
        source: sourceRef.current,
        type: GeometryType.POLYGON,
      });

      mapRef.current.addInteraction(drawRef.current);

      const snap = new Snap({ source: sourceRef.current });
      mapRef.current.addInteraction(snap);
      const modify = new Modify({ source: sourceRef.current });
      mapRef.current.addInteraction(modify);
      drawRef.current.on("drawend", () => {
        setDrawDone(true);
        if (drawRef.current) {
          mapRef.current?.removeInteraction(drawRef.current);
        }
      });
    }
  }, [createMap]);

  const removeMap = useCallback(() => {
    if (mapRef.current) {
      mapRef.current?.dispose();
      mapRef.current = undefined;
      drawRef.current = undefined;
      sourceRef.current = undefined;
    }
  }, []);

  // If mainMap, zoom to the extents of that when opening
  useEffect(() => {
    if (!mainMap || !entered) return;
    const mainExt = mainMap.getView().calculateExtent();
    mapRef.current?.getView().fit(mainExt);
  }, [mainMap, entered]);

  useEffect(() => {
    if (!entered) {
      return;
    }
    switch (activeStep) {
      case 0: {
        addMap();
        if (storePorts && mapRef.current) {
          const filteredPorts = edit
            ? storePorts.filter((p) => p.id !== port?.id)
            : storePorts;
          addPortsToMap(mapRef.current, filteredPorts);
        }
        // Zoom to the drawed feature after adding it to the map
        const ext = drawFeature?.getGeometry()?.getExtent();
        if (drawFeature && ext && mapRef.current) {
          sourceRef.current?.addFeature(drawFeature);
          mapRef.current?.getView().fit(ext);
          setDrawDone(true);
          if (drawRef.current) {
            mapRef.current?.removeInteraction(drawRef.current);
          }
        }
        break;
      }
      case 1:
        removeMap();
        break;
      case 2: {
        addMap();
        // Zoom to the drawed feature after adding it to the map
        const ext = drawFeature?.getGeometry()?.getExtent();
        if (drawFeature && ext) {
          sourceRef.current?.addFeature(drawFeature);
          mapRef.current?.getView().fit(ext);
        }
        // Make the map "readonly" by removing all interactions and controls

        // Map the controls to a separate array first to avoid mutating the
        // interactions array while looping over them when removing the interactions
        const interactionsToRemove = mapRef.current
          ?.getInteractions()
          .getArray()
          .map((i) => {
            return i;
          });
        mapRef.current
          ?.getControls()
          .forEach((c) => mapRef.current?.removeControl(c));
        interactionsToRemove?.forEach((i) =>
          mapRef.current?.removeInteraction(i),
        );
        break;
      }
      default:
        break;
    }
  }, [
    activeStep,
    addMap,
    removeMap,
    drawFeature,
    entered,
    storePorts,
    port?.id,
    edit,
  ]);

  const handleClose = () => {
    removeMap();
    onClose();
  };

  const handleNext = () => {
    setActiveStep((old) => old + 1);
  };

  const handleDrawDone = () => {
    const feat = sourceRef.current?.getFeatures()[0] as Feature<Polygon>;
    setDrawFeature(feat);
    handleNext();
  };

  const handlePrevious = () => {
    setActiveStep((old) => old - 1);
  };

  const handleSave = () => {
    // Send data to backend
    const points = drawFeature?.getGeometry()?.getCoordinates()[0];
    if (!points || !formData || !formData.locodeId) {
      return;
    }

    action
      .mutateAsync(
        edit
          ? ({
              name: formData?.name,
              description: formData?.description,
              points: points?.map((p) => ({ x: p[0], y: p[1] })),
              locodeId: formData.locodeId,
              mapAreaIds: formData.mapAreaIds,
            } as PortEditDto)
          : ({
              name: formData?.name,
              description: formData?.description,
              points: points?.map((p) => ({ x: p[0], y: p[1] })),
              locodeId: formData.locodeId,
              mapAreaIds: formData.mapAreaIds,
            } as PortCreateDto),
      )
      .then((r) => {
        enqueueSnackbar(t(`apiResponses:${r.resultKey}`), {
          variant: "success",
        });
        onActionDone();
        handleClose();
        setDrawDone(false);
        setFormData(undefined);
        setDrawFeature(undefined);
        setActiveStep(0);
      })
      .catch(apiErrorHandler);
  };

  // For the data form
  const handleSubmit = (d: PortFormInputs) => {
    setFormData(d);
    handleNext();
  };

  const handleClearDrawing = () => {
    sourceRef.current?.clear();
    // Re-add the drawing interaction
    if (drawRef.current) {
      setDrawDone(false);
      mapRef.current?.addInteraction(drawRef.current);
    }
  };

  const getContent = () => {
    switch (activeStep) {
      case 0:
        return <div ref={containerRef} className={classes.mapContainer} />;
      case 1:
        return (
          <Grid item xs={12}>
            <PortForm
              onSubmit={handleSubmit}
              defaultValues={formData}
              center={
                port?.points[0] ?? {
                  x:
                    drawFeature
                      ?.getGeometry()
                      ?.getCoordinates()?.[0]?.[0]?.[0] ?? 0,
                  y:
                    drawFeature
                      ?.getGeometry()
                      ?.getCoordinates()?.[0]?.[0]?.[1] ?? 0,
                }
              }
            />
          </Grid>
        );
      case 2:
        return (
          <>
            <Grid item xs={12} lg={6} style={{ height: "100%" }}>
              <div ref={containerRef} className={classes.mapContainer} />
            </Grid>
            <Grid item xs={12} lg={6} style={{ marginTop: theme.spacing(1) }}>
              <PortForm
                onSubmit={handleSubmit}
                defaultValues={formData}
                readOnly
                small
              />
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  const BackButton = () => {
    switch (activeStep) {
      case 0:
        return <Button onClick={onClose}>{t("cancel")}</Button>;
      case 1:
        return <Button onClick={handlePrevious}>{t("prev")}</Button>;
      case 2:
        return <Button onClick={handlePrevious}>{t("prev")}</Button>;
      default:
        return null;
    }
  };

  const NextButton = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            onClick={handleDrawDone}
            variant="contained"
            color="secondary"
            disabled={!drawDone}
          >
            {t("next")}
          </Button>
        );
      case 1:
        return (
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            form="addPortForm"
          >
            {t("next")}
          </Button>
        );
      case 2:
        return (
          <Button
            onClick={handleSave}
            disabled={action.isLoading}
            variant="contained"
            color="secondary"
          >
            {t("save")}
          </Button>
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    if (isMedium) {
      switch (activeStep) {
        case 0:
          return t("draw");
        case 1:
          return t("fillInfo");
        case 2:
          return t("confirm");
        default:
          return t("error");
      }
    }
    return edit ? t("editTitle") : t("addTitle");
  };

  return (
    <Dialog
      onClose={handleClose}
      classes={{ paper: !isMedium ? classes.root : "" }}
      open={open}
      fullWidth
      fullScreen={isMedium}
      TransitionComponent={isMedium ? Transition : undefined}
      maxWidth={!isMedium ? "lg" : undefined}
      TransitionProps={{
        onEntered: () => {
          setEntered(true);
          addMap();
        },

        onExited: () => {
          setEntered(false);
          removeMap();
        },
      }}
    >
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container className={classes.contentContainer}>
          {getContent()}
        </Grid>
        {!isMedium && (
          <Stepper activeStep={activeStep} style={{ padding: 24 }}>
            <Step>
              <StepLabel>{t("draw")}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{t("fillInfo")}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{t("confirm")}</StepLabel>
            </Step>
          </Stepper>
        )}
        <If condition={activeStep === 0}>
          <Typography className={classes.drawText}>{t("drawText")}</Typography>
        </If>
        <If condition={activeStep === 0 && !drawDone}>
          <Button
            variant="contained"
            classes={{ root: classes.drawActionButton }}
            onClick={() => drawRef.current?.removeLastPoint()}
          >
            {t("general:undo")}
          </Button>
        </If>
        <If condition={activeStep === 0 && drawDone}>
          <Button
            variant="contained"
            classes={{ root: classes.drawActionButton }}
            onClick={handleClearDrawing}
          >
            {t("clear")}
          </Button>
        </If>
      </DialogContent>
      {!isMedium ? (
        <DialogActions>
          <BackButton />
          <NextButton />
        </DialogActions>
      ) : (
        <MobileStepper
          steps={3}
          activeStep={activeStep}
          nextButton={<NextButton />}
          backButton={<BackButton />}
          variant="dots"
          position="static"
        />
      )}
    </Dialog>
  );
};

export default PortDialog;
